import React, { useCallback, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';
import {
  MAX_UPLOADED_PDF_BYTES,
  MAX_UPLOADED_STORAGE_BYTES,
  formatBytes,
} from '../../../services/uploadedDocumentsService';
import {
  clampDocumentFileName,
  DOCUMENT_TITLE_WRAP_CLASS,
} from '../../../utils/documentTitle';

const PDF_ACCEPT = 'application/pdf,.pdf';

function filterPdfFiles(fileList) {
  const files = Array.from(fileList || []);
  return files.filter((f) => {
    const name = (f.name || '').toLowerCase();
    return f.type === 'application/pdf' || name.endsWith('.pdf');
  });
}

function fileKey(file) {
  return `${file.name}-${file.size}-${file.lastModified}`;
}

function validateSelection(files, storageUsedBytes, alreadySelectedBytes) {
  const errors = [];
  const valid = [];

  for (const file of files) {
    if (file.size > MAX_UPLOADED_PDF_BYTES) {
      errors.push(`"${file.name}" excede o limite de 5 MB por arquivo.`);
      continue;
    }
    valid.push(file);
  }

  const incoming = valid.reduce((acc, f) => acc + f.size, 0);
  if (storageUsedBytes + alreadySelectedBytes + incoming > MAX_UPLOADED_STORAGE_BYTES) {
    errors.push(
      'A seleção ultrapassaria o limite de 100 MB de armazenamento por usuário.'
    );
    return { valid: [], errors };
  }

  return { valid, errors };
}

const UploadDropZone = ({
  onUpload,
  uploading = false,
  storageUsedBytes = 0,
  storageLimitBytes = MAX_UPLOADED_STORAGE_BYTES,
}) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);

  const pendingBytes = pendingFiles.reduce((acc, f) => acc + f.size, 0);

  const addFiles = useCallback(
    (fileList) => {
      setLocalError(null);
      const pdfs = filterPdfFiles(fileList);
      if (!pdfs.length) {
        setLocalError('Selecione arquivos PDF válidos.');
        return;
      }

      setPendingFiles((prev) => {
        const existingKeys = new Set(prev.map(fileKey));
        const alreadySelectedBytes = prev.reduce((acc, f) => acc + f.size, 0);
        const { valid, errors } = validateSelection(
          pdfs,
          storageUsedBytes,
          alreadySelectedBytes
        );
        if (errors.length) {
          setLocalError(errors.join(' '));
        }
        if (!valid.length) {
          return prev;
        }
        const merged = [...prev];
        for (const file of valid) {
          const key = fileKey(file);
          if (!existingKeys.has(key)) {
            merged.push(file);
            existingKeys.add(key);
          }
        }
        return merged;
      });
    },
    [storageUsedBytes]
  );

  const onInputChange = (event) => {
    addFiles(event.target.files);
    event.target.value = '';
  };

  const handleSend = async () => {
    if (!pendingFiles.length || uploading) return;
    setLocalError(null);
    const ok = await onUpload?.(pendingFiles);
    if (ok) {
      setPendingFiles([]);
    }
  };

  const handleClear = () => {
    setPendingFiles([]);
    setLocalError(null);
  };

  const openFilePicker = (e) => {
    e?.stopPropagation?.();
    inputRef.current?.click();
  };

  return (
    <div className="bg-card border border-border rounded-xl shadow-elevation-sm p-4 md:p-6 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground font-heading">
            Enviar PDFs para assinatura
          </h2>
          <p className="text-sm text-muted-foreground mt-1 font-body">
            Até {formatBytes(MAX_UPLOADED_PDF_BYTES)} por arquivo · armazenamento total{' '}
            {formatBytes(storageUsedBytes)} / {formatBytes(storageLimitBytes)}
          </p>
        </div>
      </div>

      <div
        role="presentation"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
        className={cn(
          'flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-4 py-10 transition-colors',
          dragOver
            ? 'border-primary bg-primary/5'
            : 'border-border bg-muted/20'
        )}
      >
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-muted/60">
          <Icon name="Upload" size={28} className="text-muted-foreground" />
        </div>
        <div className="text-center max-w-md">
          <p className="text-sm font-medium text-foreground font-body">
            Arraste PDFs aqui ou use o botão abaixo para selecionar
          </p>
          <p className="text-xs text-muted-foreground mt-1 font-caption">
            Vários arquivos por envio · somente PDF · máx. 5 MB cada
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          iconName="FileUp"
          disabled={uploading}
          onClick={openFilePicker}
        >
          Selecionar PDFs
        </Button>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={PDF_ACCEPT}
        multiple
        className="sr-only"
        onChange={onInputChange}
        aria-label="Selecionar PDFs"
      />

      {pendingFiles.length > 0 ? (
        <div className="rounded-lg border border-border bg-muted/20 p-3 md:p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-foreground font-body">
              {pendingFiles.length} arquivo(s) selecionado(s) ·{' '}
              {formatBytes(pendingBytes)}
            </p>
            <button
              type="button"
              onClick={handleClear}
              disabled={uploading}
              className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline disabled:opacity-50"
            >
              Limpar
            </button>
          </div>
          <ul className="max-h-32 overflow-y-auto space-y-1.5 text-sm text-muted-foreground font-body">
            {pendingFiles.map((file) => (
              <li key={fileKey(file)} className="flex items-center gap-2 min-w-0">
                <Icon name="FileText" size={16} className="shrink-0 text-muted-foreground" />
                <span
                  className={cn('min-w-0 flex-1', DOCUMENT_TITLE_WRAP_CLASS)}
                  title={file.name}
                >
                  {clampDocumentFileName(file.name)}
                </span>
                <span className="shrink-0 text-xs tabular-nums">
                  ({formatBytes(file.size)})
                </span>
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              type="button"
              variant="default"
              iconName="Upload"
              disabled={uploading}
              onClick={handleSend}
            >
              {uploading ? 'Enviando…' : 'Enviar PDFs'}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={uploading}
              onClick={openFilePicker}
            >
              Adicionar mais
            </Button>
          </div>
        </div>
      ) : null}

      {localError ? (
        <p className="text-sm text-destructive font-body break-words" role="alert">
          {localError}
        </p>
      ) : null}
    </div>
  );
};

export default UploadDropZone;
